// src/pages/finance/InvoiceDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Snackbar,
    Backdrop,
    Tooltip,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stack
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    AccessTime as AccessTimeIcon,
    Download as DownloadIcon,
    ArrowBack as ArrowBackIcon,
    PictureAsPdf as PdfIcon,
    Router as RouterIcon,
    Receipt as ReceiptIcon,
    Autorenew as AutorenewIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Mail as EmailIcon,
    ReceiptLong as TicketIcon,
    Visibility as VisibilityIcon,
    Image as ImageIcon,
    Close as CloseIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import pdfService from '../../services/pdfService';

const parseLocalDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;
  const dateStr = String(dateVal);
  const cleanStr = dateStr.split('T')[0].split(' ')[0];
  const parts = cleanStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateVal);
};


const InvoiceDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });
    const [anchorEl, setAnchorEl] = useState(null);
    const [voucherModalOpen, setVoucherModalOpen] = useState(false);
    const [selectedVoucher, setSelectedVoucher] = useState(null);

    const handleOpenVoucherModal = (voucherUrl, paymentDetails = {}) => {
        setSelectedVoucher({
            url: voucherUrl,
            details: paymentDetails
        });
        setVoucherModalOpen(true);
    };

    useEffect(() => {
        fetchInvoiceDetails();
    }, [id]);

    const fetchInvoiceDetails = async () => {
        try {
            const response = await fetch(`/api/invoices/${id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al cargar los detalles de la factura');
            }

            const data = await response.json();
            setInvoice(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            setError(error.message);
            setLoading(false);
        }
    };

    const handleVoidPayment = async (paymentId) => {
        if (!window.confirm('¿Está seguro de que desea anular este pago? Se actualizará el estado de la factura y se reactivarán las suspensiones en MikroTik si corresponde.')) {
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`/api/payments/${paymentId}/void`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Error al anular el pago');
            }

            setNotification({
                open: true,
                message: 'Pago anulado exitosamente',
                type: 'success'
            });
            
            await fetchInvoiceDetails();
        } catch (error) {
            console.error('Error voiding payment:', error);
            setNotification({
                open: true,
                message: error.message || 'Error al anular el pago',
                type: 'error'
            });
            setLoading(false);
        }
    };

    const handleDownloadPdf = async (format = 'normal') => {
        try {
            setDownloadLoading(true);

            // Obtener los datos para la descarga
            const token = localStorage.getItem('token');

            if (format === 'ticket') {
                // Impresión directa HTML en iframe sin descargar blob
                const printUrl = `/api/invoices/${id}/download?format=ticket&inline=true&html=true&t=${Date.now()}${token ? `&token=${token}` : ''}`;
                
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = '0';
                iframe.src = printUrl;
                
                document.body.appendChild(iframe);
                
                iframe.onload = () => {
                    try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                    } catch (e) {
                        console.error('Error triggering print in iframe:', e);
                        window.open(printUrl, '_blank');
                    }
                    setTimeout(() => {
                        if (document.body.contains(iframe)) {
                            document.body.removeChild(iframe);
                        }
                    }, 5000);
                };

                setNotification({
                    open: true,
                    message: 'Comprobante cargado para impresión',
                    type: 'success'
                });
                setDownloadLoading(false);
                return;
            }

            // Realizar la petición con el token incluido y el formato
            const response = await fetch(`/api/invoices/${id}/download?format=${format}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                // Si hay un error, lanzar excepción
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al descargar el PDF');
            }

            // Obtener el blob del PDF
            const pdfBlob = await response.blob();

            // Crear una URL para el blob
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Crear iframe para impresión directa
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            iframe.src = pdfUrl;

            document.body.appendChild(iframe);

            iframe.onload = () => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                } catch (e) {
                    console.error('Error triggering print in iframe:', e);
                    window.open(pdfUrl, '_blank');
                }

                setTimeout(() => {
                    if (document.body.contains(iframe)) {
                        document.body.removeChild(iframe);
                    }
                    URL.revokeObjectURL(pdfUrl);
                }, 5000);
            };

            // Mostrar notificación de éxito
            setNotification({
                open: true,
                message: 'Comprobante cargado para impresión',
                type: 'success'
            });

        } catch (error) {
            console.error('Error downloading PDF:', error);
            // Mostrar notificación de error
            setNotification({
                open: true,
                message: `Error al descargar: ${error.message}`,
                type: 'error'
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleSendEmail = async (format = 'ticket') => {
        try {
            setDownloadLoading(true);
            const response = await fetch(`/api/invoices/${id}/send-email`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ format })
            });

            const data = await response.json();

            if (response.ok) {
                setNotification({
                    open: true,
                    message: 'Comprobante enviado por correo exitosamente',
                    type: 'success'
                });
            } else {
                throw new Error(data.message || 'Error al enviar el correo');
            }
        } catch (error) {
            console.error('Error al enviar correo:', error);
            setNotification({
                open: true,
                message: `Error al enviar correo: ${error.message}`,
                type: 'error'
            });
        } finally {
            setDownloadLoading(false);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'paid':
                return <Chip label="Pagada" color="success" icon={<CheckCircleIcon />} />;
            case 'pending':
                return <Chip label="Pendiente" color="warning" icon={<AccessTimeIcon />} />;
            case 'overdue':
                return <Chip label="Vencida" color="error" icon={<WarningIcon />} />;
            default:
                return <Chip label={status} />;
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mt: 2 }}
                >
                    Volver
                </Button>
            </Box>
        );
    }

    if (!invoice) {
        return (
            <Box p={3}>
                <Alert severity="info">No se encontró la factura solicitada</Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mt: 2 }}
                >
                    Volver
                </Button>
            </Box>
        );
    }

    const getDaysDifference = (dueDateStr) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = parseLocalDate(dueDateStr);
        dueDate.setHours(0, 0, 0, 0);
        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const daysDiff = getDaysDifference(invoice.due_date);
    const isOverdue = invoice.status === 'overdue' || (daysDiff < 0 && invoice.status === 'pending');
    const hasPayment = invoice.payments && invoice.payments.length > 0;
    const isActive = invoice.client_status === 'active';
    const isSyncPending = invoice.mikrotik_sync && invoice.mikrotik_sync.status === 'pending';
    const isSyncError = invoice.mikrotik_sync && invoice.mikrotik_sync.status === 'failed';

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                >
                    Volver
                </Button>
                <Box>
                    <Button
                        variant="contained"
                        startIcon={downloadLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        disabled={downloadLoading}
                        color="primary"
                        sx={{
                            backgroundColor: '#3b82f6',
                            '&:hover': {
                                backgroundColor: '#2563eb',
                            },
                            transition: 'all 0.3s',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        }}
                    >
                        Opciones de Comprobante
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        PaperProps={{
                            sx: {
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                border: '1px solid rgba(0,0,0,0.05)',
                                mt: 0.5
                            }
                        }}
                    >
                        <MenuItem
                            onClick={() => {
                                handleDownloadPdf('normal');
                                setAnchorEl(null);
                            }}
                            sx={{ gap: 1.5, py: 1 }}
                        >
                            <PdfIcon fontSize="small" sx={{ color: '#ef4444' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Descargar PDF (A4)</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                handleDownloadPdf('ticket');
                                setAnchorEl(null);
                            }}
                            sx={{ gap: 1.5, py: 1 }}
                        >
                            <TicketIcon fontSize="small" sx={{ color: '#0ea5e9' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Descargar Ticket (80mm)</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                if (invoice.client_email) {
                                    handleSendEmail('ticket');
                                } else {
                                    alert('El cliente no tiene un correo electrónico registrado.');
                                }
                                setAnchorEl(null);
                            }}
                            sx={{ gap: 1.5, py: 1 }}
                        >
                            <EmailIcon fontSize="small" sx={{ color: '#6366f1' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>Enviar por Correo</Typography>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            <Paper elevation={2} sx={{
                p: 3,
                mb: 3,
                borderRadius: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                border: '1px solid rgba(0,0,0,0.05)',
                overflow: 'hidden'
            }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                            Recibo #{invoice.invoice_number}
                        </Typography>
                        <Box mt={1}>
                            {getStatusChip(invoice.status)}
                        </Box>
                        <Typography variant="body2" color="textSecondary" mt={1}>
                            Creada: {format(parseLocalDate(invoice.created_at), 'dd/MM/yyyy')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#3b82f6' }}>
                            Total: ${parseFloat(invoice.amount).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Vencimiento: {format(parseLocalDate(invoice.due_date), 'dd/MM/yyyy')}
                        </Typography>
                        {invoice.payment_promise_date && (
                            <Typography variant="body2" color="textSecondary">
                                Promesa de pago: {format(parseLocalDate(invoice.payment_promise_date), 'dd/MM/yyyy')}
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                            Cliente
                        </Typography>
                        <Typography>{invoice.client_name}</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Email: {invoice.client_email || 'No disponible'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Teléfono: {invoice.client_phone || 'No disponible'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Estado del servicio: <Chip
                                size="small"
                                label={invoice.client_status === 'active' ? 'Activo' : 'Suspendido'}
                                color={invoice.client_status === 'active' ? 'success' : 'error'}
                            />
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                            Resumen de pago
                        </Typography>
                        <Box sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', p: 2, borderRadius: '8px' }}>
                            <Typography variant="body1" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Monto total:</span>
                                <span>${parseFloat(invoice.amount).toFixed(2)}</span>
                            </Typography>
                            <Typography variant="body1" sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                                <span>Pagado:</span>
                                <span style={{ color: '#10b981' }}>${parseFloat(invoice.paid_amount || 0).toFixed(2)}</span>
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body1" sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                <span>Pendiente:</span>
                                <span style={{ color: invoice.status === 'paid' ? '#10b981' : '#f59e0b' }}>
                                    ${(parseFloat(invoice.amount) - parseFloat(invoice.paid_amount || 0)).toFixed(2)}
                                </span>
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Línea de Tiempo del Recibo y Sincronización MikroTik */}
            <Paper elevation={2} sx={{
                p: 4,
                mb: 3,
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.04)'
            }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, color: '#0f172a', mb: 3 }}>
                    Trazabilidad de Recibo e Integración MikroTik
                </Typography>
                <Grid container spacing={3}>
                    {/* Paso 1: Generación */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            p: 2.5,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(99, 102, 241, 0.05)',
                            border: '1px solid rgba(99, 102, 241, 0.1)',
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-2px)' }
                        }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                color: '#6366f1',
                                mb: 1.5,
                                boxShadow: '0 0 10px rgba(99, 102, 241, 0.1)'
                            }}>
                                <ReceiptIcon fontSize="medium" />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                                1. Generación
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                                Emitido: {format(parseLocalDate(invoice.created_at), 'dd/MM/yyyy')}
                            </Typography>
                            <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 600 }}>
                                Listo
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Paso 2: Vencimiento */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            p: 2.5,
                            borderRadius: '12px',
                            backgroundColor: isOverdue ? 'rgba(239, 68, 68, 0.05)' : 'rgba(245, 158, 11, 0.05)',
                            border: isOverdue ? '1px solid rgba(239, 68, 68, 0.1)' : '1px solid rgba(245, 158, 11, 0.1)',
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-2px)' }
                        }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isOverdue ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                color: isOverdue ? '#ef4444' : '#f59e0b',
                                mb: 1.5,
                                boxShadow: isOverdue ? '0 0 10px rgba(239, 68, 68, 0.1)' : '0 0 10px rgba(245, 158, 11, 0.1)'
                            }}>
                                <HourglassEmptyIcon fontSize="medium" />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                                2. Vencimiento
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                                Límite: {format(parseLocalDate(invoice.due_date), 'dd/MM/yyyy')}
                            </Typography>
                            
                            {invoice.status === 'paid' ? (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 600 }}>
                                    Pagado a tiempo
                                </Typography>
                            ) : invoice.payment_promise_date ? (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', fontWeight: 600 }}>
                                    Prórroga: {format(parseLocalDate(invoice.payment_promise_date), 'dd/MM/yyyy')}
                                </Typography>
                            ) : daysDiff < 0 ? (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', fontWeight: 600 }}>
                                    Vencido hace {Math.abs(daysDiff)} días
                                </Typography>
                            ) : daysDiff === 0 ? (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', fontWeight: 600 }}>
                                    Vence hoy
                                </Typography>
                            ) : (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', fontWeight: 600 }}>
                                    Faltan {daysDiff} días
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    {/* Paso 3: Reporte de Pago */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            p: 2.5,
                            borderRadius: '12px',
                            backgroundColor: hasPayment ? 'rgba(16, 185, 129, 0.05)' : 'rgba(100, 116, 139, 0.05)',
                            border: hasPayment ? '1px solid rgba(16, 185, 129, 0.1)' : '1px solid rgba(100, 116, 139, 0.1)',
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-2px)' }
                        }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: hasPayment ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                                color: hasPayment ? '#10b981' : '#64748b',
                                mb: 1.5,
                                boxShadow: hasPayment ? '0 0 10px rgba(16, 185, 129, 0.1)' : '0 0 10px rgba(100, 116, 139, 0.1)'
                            }}>
                                <CheckCircleIcon fontSize="medium" />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                                3. Reporte de Pago
                            </Typography>
                            <Box sx={{ mt: 0.5, minHeight: '34px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                {hasPayment ? (
                                    <>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                            {invoice.payments[0].payment_method_name || 'Registrado'}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                            Ref: {invoice.payments[0].payment_reference || 'N/A'}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                        Sin reportar aún
                                    </Typography>
                                )}
                            </Box>
                            
                            {hasPayment ? (
                                invoice.payments[0].status === 'completed' || invoice.payments[0].status === 'approved' ? (
                                    <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 600 }}>
                                        Aprobado
                                    </Typography>
                                ) : invoice.payments[0].status === 'rejected' ? (
                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', fontWeight: 600 }}>
                                            Rechazado
                                        </Typography>
                                        {invoice.payments[0].notes && (
                                            <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.65rem', color: '#ef4444', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                Obs: "{invoice.payments[0].notes}"
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', fontWeight: 600 }}>
                                        Pendiente revisión
                                    </Typography>
                                )
                            ) : (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(100, 116, 139, 0.1)', color: '#64748b', fontWeight: 600 }}>
                                    Espera
                                </Typography>
                            )}

                            {hasPayment && invoice.payments[0].proof_image_url && (
                                <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<ImageIcon sx={{ fontSize: '0.9rem' }} />}
                                    onClick={() => handleOpenVoucherModal(invoice.payments[0].proof_image_url, {
                                        method: invoice.payments[0].payment_method_name,
                                        reference: invoice.payments[0].payment_reference,
                                        amount: invoice.payments[0].amount
                                    })}
                                    sx={{ mt: 0.5, fontSize: '0.7rem', textTransform: 'none', py: 0.2, fontWeight: 700, color: '#0284c7' }}
                                >
                                    Ver Voucher
                                </Button>
                            )}
                        </Box>
                    </Grid>

                    {/* Paso 4: Estado de Red (MikroTik) */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            p: 2.5,
                            borderRadius: '12px',
                            backgroundColor: isActive ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                            border: isActive ? '1px solid rgba(16, 185, 129, 0.1)' : '1px solid rgba(239, 68, 68, 0.1)',
                            height: '100%',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'translateY(-2px)' }
                        }}>
                            <Box sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: isActive ? '#10b981' : '#ef4444',
                                mb: 1.5,
                                boxShadow: isActive ? '0 0 10px rgba(16, 185, 129, 0.1)' : '0 0 10px rgba(239, 68, 68, 0.1)'
                            }}>
                                <RouterIcon fontSize="medium" />
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#334155' }}>
                                4. Sincronización MikroTik
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                                IP: {invoice.client_ip || 'Sin asignar'}
                            </Typography>
                            
                            {isActive ? (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 600 }}>
                                    Servicio Activo
                                </Typography>
                            ) : (
                                <Typography variant="caption" sx={{ mt: 1, display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', fontWeight: 600 }}>
                                    Servicio Suspendido
                                </Typography>
                            )}

                            {/* Detalle de cola en segundo plano */}
                            {invoice.mikrotik_sync && (
                                <Box sx={{ mt: 1, width: '100%' }}>
                                    {isSyncPending && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            <AutorenewIcon sx={{ fontSize: '0.9rem', color: '#f59e0b', animation: 'spin 2s linear infinite' }} />
                                            <Typography variant="caption" sx={{ color: '#f59e0b', fontSize: '0.65rem', fontWeight: 600 }}>
                                                Cola pendiente (Intento: {invoice.mikrotik_sync.attempts})
                                            </Typography>
                                        </Box>
                                    )}
                                    {isSyncError && (
                                        <Tooltip title={invoice.mikrotik_sync.last_error || 'Error desconocido'}>
                                            <Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.65rem', fontWeight: 600, display: 'block', cursor: 'help' }}>
                                                ⚠️ Fallo de conexión (Reintentando)
                                            </Typography>
                                        </Tooltip>
                                    )}
                                    {!isSyncPending && !isSyncError && (
                                        <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.65rem', fontWeight: 600 }}>
                                            ✓ Sincronía física OK
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Detalles de items */}
            {invoice.items && invoice.items.length > 0 && (
                <Paper elevation={2} sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0,0,0,0.05)',
                }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                        Detalles
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
                                    <TableCell>Descripción</TableCell>
                                    <TableCell align="right">Cantidad</TableCell>
                                    <TableCell align="right">Precio</TableCell>
                                    <TableCell align="right">Subtotal</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoice.items.map((item, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell align="right">{item.quantity}</TableCell>
                                        <TableCell align="right">
                                            ${(parseFloat(item.amount) / item.quantity).toFixed(2)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            ${parseFloat(item.amount).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
                                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#3b82f6' }}>
                                        ${parseFloat(invoice.amount).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Sección Destacada: Detalle del Pago y Captura de Voucher (Yape/Plin) */}
            {invoice.payments && invoice.payments.length > 0 && (
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(2, 132, 199, 0.1)', color: '#0284c7', display: 'flex' }}>
                                <ImageIcon fontSize="medium" />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.3px' }}>
                                    Detalle del Pago y Captura de Comprobante (Yape / Plin)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Información de la transacción e imagen adjunta del voucher reportado.
                                </Typography>
                            </Box>
                        </Box>
                        <Chip
                            label={invoice.payments[0].status === 'completed' || invoice.payments[0].status === 'approved' ? 'Pago Aprobado' : 'Pendiente'}
                            color={invoice.payments[0].status === 'completed' || invoice.payments[0].status === 'approved' ? 'success' : 'warning'}
                            sx={{ fontWeight: 700, borderRadius: '8px' }}
                        />
                    </Box>

                    <Grid container spacing={3} alignItems="stretch">
                        {/* Columna Izquierda: Información Detallada del Pago */}
                        <Grid item xs={12} md={7}>
                            <Box sx={{ p: 2.5, bgcolor: '#ffffff', borderRadius: '12px', border: '1px solid #f1f5f9', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Método de Pago</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 800, color: '#0284c7', mt: 0.2 }}>
                                            {invoice.payments[0].payment_method_name || 'Billetera Móvil'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Monto Cobrado</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 800, color: '#16a34a', mt: 0.2 }}>
                                            S/ {parseFloat(invoice.payments[0].amount).toFixed(2)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>N° Operación / Ref</Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b', mt: 0.2, fontFamily: 'monospace' }}>
                                            {invoice.payments[0].payment_reference || 'Sin Ref.'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Fecha de Registro</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.2 }}>
                                            {format(new Date(invoice.payments[0].payment_date), 'dd/MM/yyyy HH:mm')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Abonado / Cliente</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.2 }}>
                                            {invoice.client_name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Estado de Servicio</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0ea5e9', mt: 0.2 }}>
                                            Servicio Activo
                                        </Typography>
                                    </Grid>

                                    {invoice.payments[0].notes && (
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 1 }} />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Notas / Observaciones</Typography>
                                            <Typography variant="body2" sx={{ color: '#475569', fontStyle: 'italic', mt: 0.2 }}>
                                                "{invoice.payments[0].notes}"
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>

                        {/* Columna Derecha: Captura FÍSICA del Comprobante / Voucher Yape / Plin */}
                        <Grid item xs={12} md={5}>
                            <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: '12px', border: '1px solid #f1f5f9', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                {invoice.payments[0].proof_image_url ? (
                                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            📸 Captura de Yape / Plin / Voucher
                                        </Typography>
                                        <Box
                                            onClick={() => handleOpenVoucherModal(invoice.payments[0].proof_image_url, {
                                                method: invoice.payments[0].payment_method_name,
                                                reference: invoice.payments[0].payment_reference,
                                                amount: invoice.payments[0].amount,
                                                date: invoice.payments[0].payment_date
                                            })}
                                            sx={{
                                                position: 'relative',
                                                cursor: 'pointer',
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                border: '2px solid #e2e8f0',
                                                maxHeight: '260px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: '#0f172a',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    borderColor: '#0284c7',
                                                    boxShadow: '0 8px 20px rgba(2, 132, 199, 0.2)',
                                                    '& .hover-overlay': { opacity: 1 }
                                                }
                                            }}
                                        >
                                            <img
                                                src={invoice.payments[0].proof_image_url}
                                                alt="Voucher de Pago"
                                                style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain' }}
                                            />
                                            <Box
                                                className="hover-overlay"
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    bgcolor: 'rgba(15, 23, 42, 0.6)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#ffffff',
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s'
                                                }}
                                            >
                                                <VisibilityIcon sx={{ fontSize: 32, mb: 0.5 }} />
                                                <Typography variant="caption" sx={{ fontWeight: 700 }}>Clic para Ampliar Voucher</Typography>
                                            </Box>
                                        </Box>
                                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1.5 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => handleOpenVoucherModal(invoice.payments[0].proof_image_url, {
                                                    method: invoice.payments[0].payment_method_name,
                                                    reference: invoice.payments[0].payment_reference,
                                                    amount: invoice.payments[0].amount,
                                                    date: invoice.payments[0].payment_date
                                                })}
                                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                                            >
                                                Ver Imagen Completa
                                            </Button>
                                            <Button
                                                component="a"
                                                href={invoice.payments[0].proof_image_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="text"
                                                size="small"
                                                startIcon={<OpenInNewIcon />}
                                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                            >
                                                Abrir Pestaña
                                            </Button>
                                        </Stack>
                                    </Box>
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center', color: '#94a3b8' }}>
                                        <ImageIcon sx={{ fontSize: 48, opacity: 0.5, mb: 1 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Sin captura de voucher adjunta</Typography>
                                        <Typography variant="caption" display="block">El pago fue registrado manualmente sin imagen.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Historial de pagos */}
            {invoice.payments && invoice.payments.length > 0 && (
                <Paper elevation={2} sx={{
                    p: 3,
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0,0,0,0.05)',
                }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                        Historial de pagos
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Método</TableCell>
                                    <TableCell>Referencia</TableCell>
                                    <TableCell>Estado</TableCell>
                                    <TableCell align="right">Monto</TableCell>
                                    <TableCell align="center">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {invoice.payments.map((payment, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            {format(new Date(payment.payment_date), 'dd/MM/yyyy HH:mm')}
                                        </TableCell>
                                        <TableCell>{payment.payment_method_name}</TableCell>
                                        <TableCell>{payment.payment_reference || '-'}</TableCell>
                                        <TableCell>
                                            {payment.status === 'completed' || payment.status === 'approved' ? (
                                                <Chip label="Completado" size="small" color="success" />
                                            ) : payment.status === 'rejected' ? (
                                                <Chip label="Rechazado" size="small" color="error" />
                                            ) : payment.status === 'voided' ? (
                                                <Chip label="Anulado" size="small" sx={{ backgroundColor: '#f1f5f9', color: '#475569' }} />
                                            ) : (
                                                <Chip label="Pendiente" size="small" color="warning" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            ${parseFloat(payment.amount).toFixed(2)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                                {payment.proof_image_url && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() => handleOpenVoucherModal(payment.proof_image_url, {
                                                            method: payment.payment_method_name,
                                                            reference: payment.payment_reference,
                                                            amount: payment.amount
                                                        })}
                                                        sx={{
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            fontSize: '0.75rem',
                                                            backgroundColor: '#0284c7',
                                                            '&:hover': { backgroundColor: '#0369a1' }
                                                        }}
                                                    >
                                                        Ver Captura
                                                    </Button>
                                                )}
                                                {payment.status !== 'voided' && (
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleVoidPayment(payment.id)}
                                                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                                    >
                                                        Anular
                                                    </Button>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ backgroundColor: 'rgba(59, 130, 246, 0.05)' }}>
                                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>Total Pagado</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                                        ${invoice.payments.reduce((sum, payment) => {
                                            if (payment.status === 'completed' || payment.status === 'approved') {
                                                return sum + parseFloat(payment.amount);
                                            }
                                            return sum;
                                        }, 0).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* Notificaciones */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                message={notification.message}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.type}
                    sx={{ width: '100%', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>

            {/* Backdrop para la carga de descarga */}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={downloadLoading}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    p: 3,
                    borderRadius: '8px'
                }}>
                    <CircularProgress color="inherit" />
                    <Typography sx={{ mt: 2 }}>Generando PDF...</Typography>
                </Box>
            </Backdrop>

            {/* Modal para Visualizar Captura / Voucher de Pago */}
            <Dialog
                open={voucherModalOpen}
                onClose={() => setVoucherModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon sx={{ color: '#0284c7' }} />
                        <Typography variant="h6" fontWeight={700}>
                            Captura de Comprobante / Voucher
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setVoucherModalOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ textAlign: 'center', py: 3, bgcolor: '#f8fafc' }}>
                    {selectedVoucher ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid #e2e8f0', p: 1, bgcolor: '#ffffff' }}>
                                <img
                                    src={selectedVoucher.url}
                                    alt="Voucher de Pago"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '65vh',
                                        objectFit: 'contain',
                                        borderRadius: '8px'
                                    }}
                                />
                            </Paper>
                            {selectedVoucher.details && (
                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
                                    {selectedVoucher.details.method && (
                                        <Chip label={`Método: ${selectedVoucher.details.method}`} color="primary" variant="outlined" size="small" />
                                    )}
                                    {selectedVoucher.details.reference && (
                                        <Chip label={`Ref: ${selectedVoucher.details.reference}`} color="secondary" variant="outlined" size="small" />
                                    )}
                                    {selectedVoucher.details.amount && (
                                        <Chip label={`Monto: S/ ${parseFloat(selectedVoucher.details.amount).toFixed(2)}`} color="success" size="small" />
                                    )}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">No se encontró la imagen del comprobante.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
                    {selectedVoucher?.url ? (
                        <Button
                            component="a"
                            href={selectedVoucher.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNewIcon />}
                            size="small"
                        >
                            Abrir Imagen Completa
                        </Button>
                    ) : <div />}
                    <Button variant="contained" onClick={() => setVoucherModalOpen(false)}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InvoiceDetail;